<template>
  <div v-if="chapter" class="configurator">
    <chapter-configurator-general :chapter="chapter" @input="onInput" />
    <chapter-configurator-managers :chapter="chapter" @input="onInput" />
    <chapter-configurator-images :chapter="chapter" @input="onInput" />

    <configuration-tray-buttons
      :disabled="saving || chapter.inArchivedSeason"
      :show-delete-button="isDeletable"
      @saved="save"
      @canceled="onCanceled"
      @deleted="confirmDeletion = true"
    />

    <confirmation-modal
      modal-id="modal-target-delete-chapter"
      :show-modal="confirmDeletion"
      :confirmation="lang.get('chapters.alerts.delete')"
      :confirm-button-label="lang.get('buttons.delete')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmDeletion = false"
      @confirmed="remove"
    />

    <confirmation-modal
      modal-id="modal-target-cancel-chapter-configuration"
      :show-modal="confirmCancellation"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmCancellation = false"
      @confirmed="cancel"
    />
  </div>
</template>

<script>
import _ from 'underscore';
import { mapActions, mapMutations, mapState } from 'vuex';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import ConfigurationTrayButtons from '../ConfigurationTrayButtons';
import ChapterConfiguratorGeneral from './ChapterConfiguratorGeneral';
import ChapterConfiguratorManagers from './ChapterConfiguratorManagers';
import ChapterConfiguratorImages from './ChapterConfiguratorImages';

export default {
  inject: ['lang'],
  components: {
    ConfirmationModal,
    ConfigurationTrayButtons,
    ChapterConfiguratorGeneral,
    ChapterConfiguratorManagers,
    ChapterConfiguratorImages
  },
  props: {
    chapter: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      confirmCancellation: false,
      confirmDeletion: false
    };
  },
  computed: {
    ...mapState('entryFormConfiguration', [
      'configurationInProgress',
      'saving'
    ]),
    isDeletable() {
      return this.chapter.createdAt !== null && !this.chapter.locked;
    }
  },
  created() {
    if (this.chapter.createdAt) {
      this.preserveChapter({ ...this.chapter });
    }

    this.debouncedUpdate = _.debounce(this.updateChapter, 300);

    this.$emit('created');
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'configureChapter',
      'addChapter',
      'saveChapter',
      'deleteChapter'
    ]),
    ...mapMutations('entryForm', [
      'updateChapter',
      'removeChapter',
      'updateChapterId'
    ]),
    ...mapMutations('entryFormConfiguration', [
      'preserveChapter',
      'setConfigurationInProgressFlag'
    ]),
    onInput(input) {
      if (!this.configurationInProgress) {
        this.setConfigurationInProgressFlag(true);
      }

      this.debouncedUpdate({ ...this.chapter, ...input });
    },
    save() {
      if (!this.chapter.createdAt) {
        this.addChapter({ chapter: this.chapter });
      } else {
        this.saveChapter({ chapter: this.chapter });
      }
    },
    onCanceled() {
      if (this.configurationInProgress) {
        this.confirmCancellation = true;
      } else {
        this.cancel();
      }
    },
    cancel() {
      this.confirmCancellation = false;
      this.$nextTick(() => {
        this.configureChapter({ slug: null });
      });
    },
    remove() {
      this.confirmDeletion = false;

      this.deleteChapter({ chapter: this.chapter }).then(response => {
        if (response && response.status === 200) {
          this.configureChapter({ slug: null });
          this.removeChapter(this.chapter.slug);
          this.updateChapterId(null);
        }
      });
    }
  }
};
</script>

<style scoped>
.configurator {
  margin-bottom: 150px;
}
</style>
